import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, Plus, Sparkles, Workflow as WorkflowIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { RunList } from '../components/automation-runs';
import { useConditionOptions } from '../components/ConditionBuilder';
import { RecurringTicketsPanel } from '../components/RecurringTicketsPanel';
import { SmartRulesPanel } from '../components/SmartRulesPanel';
import { useToast } from '../components/toast';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  PageLoader,
  Pagination,
  Select,
  Switch,
  Table,
  Tabs,
  Td,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { describeDuration, RUN_STATUS_LABEL, TIME_TRIGGERS, TRIGGER_LABEL, useWorkflows } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { formatRelative } from '../lib/format';
import { useUrlState } from '../lib/hooks';
import type { AutomationKind, AutomationRun, AutomationRunStatus, Page, WorkflowListItem } from '../lib/types';

function runSummary(stats: WorkflowListItem['runsLast7Days']) {
  const parts = [
    stats.SUCCEEDED && `${stats.SUCCEEDED} ran`,
    stats.WAITING && `${stats.WAITING} waiting`,
    stats.FAILED && `${stats.FAILED} failed`,
    stats.SKIPPED && `${stats.SKIPPED} skipped`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No runs';
}

function StarterPackButton() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const install = useMutation({
    mutationFn: () => api.post<{ departments: number; workflows: number; smartRules: number; recurringTickets: number }>('/automation/starter-pack'),
    onSuccess: (result) => {
      for (const key of ['workflows', 'smart-rules', 'recurring-tickets', 'departments']) void queryClient.invalidateQueries({ queryKey: [key] });
      const added = result.workflows + result.smartRules + result.recurringTickets + result.departments;
      toast.success(
        added
          ? `Added ${result.workflows} workflows, ${result.smartRules} smart rules, ${result.recurringTickets} schedules, and ${result.departments} departments. They’re turned off until you review them.`
          : 'The examples are already here',
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Button icon={<Sparkles />} loading={install.isPending} onClick={() => install.mutate()}>
      Add examples
    </Button>
  );
}

function WorkflowsTab() {
  const { can } = useAuth();
  const canEdit = can('ADMIN');
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const workflows = useWorkflows();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workflows'] });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.patch(`/workflows/${id}`, { enabled }),
    onSuccess: (_result, { enabled }) => {
      void refresh();
      toast.success(enabled ? 'Workflow turned on' : 'Workflow turned off');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.post('/workflows/reorder', { ids }),
    onSuccess: () => void refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (workflows.isLoading) return <PageLoader />;
  if (workflows.error) return <ErrorState error={workflows.error} onRetry={() => workflows.refetch()} />;
  const items = workflows.data!;

  const move = (index: number, by: number) => {
    const ids = items.map((w) => w.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + by, 0, id);
    queryClient.setQueryData<WorkflowListItem[]>(['workflows'], ids.map((wid) => items.find((w) => w.id === wid)!));
    reorder.mutate(ids);
  };

  if (items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<WorkflowIcon />}
          title="No workflows yet"
          description="Workflows do the triage your team does by hand: route new tickets, assign them in turn, and escalate the ones that wait too long."
          action={
            canEdit && (
              <div className="flex flex-wrap justify-center gap-2">
                <StarterPackButton />
                <Button variant="primary" icon={<Plus />} onClick={() => navigate('/automation/workflows/new')}>
                  New workflow
                </Button>
              </div>
            )
          }
        />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Workflows" description="Checked top to bottom for each event. A workflow set to stop keeps later ones from running." />
      <Table>
        <thead>
          <tr>
            {canEdit && <Th className="w-16">Order</Th>}
            <Th>Workflow</Th>
            <Th>When</Th>
            <Th>Last 7 days</Th>
            <Th>Last run</Th>
            <Th className="text-right">On</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((workflow, index) => (
            <tr key={workflow.id} className="cursor-pointer hover:bg-slate-50/70" onClick={() => navigate(`/automation/workflows/${workflow.id}`)}>
              {canEdit && (
                <Td>
                  <div className="flex" onClick={(e) => e.stopPropagation()}>
                    <button type="button" disabled={index === 0} onClick={() => move(index, -1)} className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Move up">
                      <ArrowUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      disabled={index === items.length - 1}
                      onClick={() => move(index, 1)}
                      className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ArrowDown className="size-4" />
                    </button>
                  </div>
                </Td>
              )}
              <Td className="max-w-md">
                <Link to={`/automation/workflows/${workflow.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                  {workflow.name}
                </Link>
                {workflow.description && <p className="truncate text-xs text-slate-500">{workflow.description}</p>}
                {!workflow.valid && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-red-700">
                    <AlertTriangle className="size-3" /> Needs attention: open it and save again
                  </p>
                )}
              </Td>
              <Td className="text-slate-700">
                {TRIGGER_LABEL[workflow.trigger]}
                {TIME_TRIGGERS.includes(workflow.trigger) && <p className="text-xs text-slate-500">after {describeDuration(workflow.triggerConfig)}</p>}
              </Td>
              <Td className="text-xs whitespace-nowrap text-slate-600">{runSummary(workflow.runsLast7Days)}</Td>
              <Td className="whitespace-nowrap text-slate-500">{workflow.lastRunAt ? formatRelative(workflow.lastRunAt) : 'Never'}</Td>
              <Td className="text-right">
                <div className="inline-flex" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={workflow.enabled}
                    disabled={!canEdit || toggle.isPending}
                    onChange={(enabled) => toggle.mutate({ id: workflow.id, enabled })}
                    label={<span className="sr-only">Turn {workflow.enabled ? 'off' : 'on'}</span>}
                  />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

const RUN_PAGE_SIZE = 30;

function RunHistoryTab() {
  const options = useConditionOptions();
  const [filters, setFilters] = useUrlState({ tab: 'runs', kind: '', status: '', page: '1' });
  const page = Number(filters.page) || 1;
  const runs = useQuery({
    queryKey: ['automation-runs', 'all', filters.kind, filters.status, page],
    queryFn: () =>
      api.get<Page<AutomationRun>>('/automation/runs', {
        kind: filters.kind || undefined,
        status: filters.status || undefined,
        page,
        pageSize: RUN_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });

  return (
    <Card>
      <CardHeader title="Run history" description="Every workflow, smart rule, and schedule run, with what it did and why." />
      <div className="flex flex-wrap gap-2 border-b border-slate-100 p-3">
        <Select value={filters.kind} onChange={(e) => setFilters({ kind: e.target.value })} className="w-auto">
          <option value="">Everything</option>
          {(['WORKFLOW', 'SMART_RULE', 'RECURRING'] as AutomationKind[]).map((kind) => (
            <option key={kind} value={kind}>
              {{ WORKFLOW: 'Workflows', SMART_RULE: 'Smart rules', RECURRING: 'Scheduled tickets' }[kind]}
            </option>
          ))}
        </Select>
        <Select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} className="w-auto">
          <option value="">Any result</option>
          {(Object.keys(RUN_STATUS_LABEL) as AutomationRunStatus[]).map((status) => (
            <option key={status} value={status}>
              {RUN_STATUS_LABEL[status]}
            </option>
          ))}
        </Select>
      </div>
      {runs.isLoading ? (
        <PageLoader />
      ) : runs.error ? (
        <ErrorState error={runs.error} onRetry={() => runs.refetch()} />
      ) : runs.data!.items.length === 0 ? (
        <EmptyState title="No runs yet" description="Runs appear here as soon as workflows, smart rules, or schedules do something." />
      ) : (
        <>
          <RunList runs={runs.data!.items} options={options} />
          <Pagination page={page} pageSize={RUN_PAGE_SIZE} total={runs.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
        </>
      )}
    </Card>
  );
}

type Tab = 'workflows' | 'smart-rules' | 'schedules' | 'runs';

export function AutomationPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useUrlState({ tab: 'workflows' });
  const tab = (['workflows', 'smart-rules', 'schedules', 'runs'].includes(state.tab) ? state.tab : 'workflows') as Tab;

  return (
    <>
      <PageHeader
        title="Automation"
        description="Workflows route, assign, and escalate tickets. Smart rules and schedules open tickets on their own."
        actions={
          can('ADMIN') &&
          tab === 'workflows' && (
            <>
              <StarterPackButton />
              <Button variant="primary" icon={<Plus />} onClick={() => navigate('/automation/workflows/new')}>
                New workflow
              </Button>
            </>
          )
        }
      />
      <Tabs<Tab>
        className="mb-6"
        value={tab}
        onChange={(value) => setState({ tab: value })}
        tabs={[
          { value: 'workflows', label: 'Workflows' },
          { value: 'smart-rules', label: 'Smart rules' },
          { value: 'schedules', label: 'Scheduled tickets' },
          { value: 'runs', label: 'Run history' },
        ]}
      />
      {tab === 'workflows' && <WorkflowsTab />}
      {tab === 'smart-rules' && <SmartRulesPanel />}
      {tab === 'schedules' && <RecurringTicketsPanel />}
      {tab === 'runs' && <RunHistoryTab />}
    </>
  );
}
